import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useAuthStore from "../../stores/useAuthStore";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";
import { axiosget, axiospost, axiosdelete } from "../../helpers/httprequest";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";

// --- Icons ---
const BookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const WeightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.91 8.84 8.56 2.23a1.93 1.93 0 0 0-1.81 0L3.1 4.13a2.12 2.12 0 0 0-.05 3.69l12.35 6.61a1.93 1.93 0 0 0 1.81 0l3.65-1.9a2.12 2.12 0 0 0 .05-3.69Z"></path>
    <path d="M12 10v9.5c0 .32.14.63.4.82l6.5 4.54a1.05 1.05 0 0 0 1.6-1V13.8"></path>
  </svg>
);

const Registration = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const maxUnits = 24; // As per requirement

  const { data, isLoading, error } = useQuery({
    queryKey: ["registerCourses"],
    queryFn: async () => {
      const req = await axiosget(`ms_course_registration`);
      return req?.data || req;
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (courseId) => {
      const res = await axiospost(`ms_course_registration`, { courseId });
      return res?.data || res;
    },
    onSuccess: (res) => {
      if (res.type === 'success') {
        toast.success(res.message);
        queryClient.invalidateQueries({ queryKey: ["registerCourses"] });
      } else {
        toast.error(res.message);
      }
    },
    onError: (err) => {
      toast.error(err.message || "Failed to register course");
    }
  });

  const dropMutation = useMutation({
    mutationFn: async (courseId) => {
      const res = await axiosdelete(`ms_course_registration/${courseId}`);
      return res?.data || res;
    },
    onSuccess: (res) => {
      if (res.type === 'success') {
        toast.success(res.message);
        queryClient.invalidateQueries({ queryKey: ["registerCourses"] });
      } else {
        toast.error(res.message);
      }
    },
    onError: (err) => {
      toast.error(err.message || "Failed to drop course");
    }
  });

  const handleRegister = (courseId) => {
    registerMutation.mutate(courseId);
  };

  const handleDrop = (courseId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to drop this course?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, drop it!"
    })
      .then((result) => {
        if (result.isConfirmed) {
          dropMutation.mutate(courseId);
        }
      });
  };

  if (isLoading) return <Loader />;
  if (error) return <ErrorResponse type={`danger`} message={`Local server error: ${error}`} />;
  if (data?.type !== undefined && data?.type === 'error') return <ErrorResponse type={`danger`} message={`${data?.message}`} />;

  // Calculate registered units
  const registeredUnits = data?.reduce((acc, curr) => {
    return curr.isRegistered ? acc + parseInt(curr.credit || 0) : acc;
  }, 0) || 0;

  const progressPercentage = Math.min((registeredUnits / maxUnits) * 100, 100);
  const initials = user ? `${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}` : 'ST';

  return (
    <div className="bg-white min-h-screen p-8 font-sans">
      {/* Header Section */}
      <div className="flex gap-8 mb-12 flex-wrap">
        {/* Profile Card */}
        <div className="flex-1 min-w-[300px] bg-white rounded-[20px] p-8 flex flex-col items-center text-center shadow-[0_4px_20px_rgba(0,0,0,0.05)] relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[100px] bg-gradient-to-br from-violet-400 to-violet-500 z-0"></div>
          {/* Avatar */}
          <div className="w-[100px] h-[100px] rounded-full bg-gray-200 flex items-center justify-center text-[2.5rem] font-bold text-gray-600 border-4 border-white shadow-[0_4px_10px_rgba(0,0,0,0.1)] z-[1] mb-4">
            {initials}
          </div>
          <h3 className="text-2xl font-bold text-gray-800 m-0 z-[1]">
            {user ? `${user.firstname || ''} ${user.middlename || ''} ${user.lastname || ''}` : 'Student Name'}
          </h3>
          <p className="text-gray-500 mt-2 font-medium z-[1]">
            {user?.studentid || user?.matricno || 'Matric Number'}
          </p>
        </div>

        {/* Progress Card */}
        <div className="flex-[1.5] min-w-[300px] bg-white rounded-[20px] p-10 shadow-[0_4px_20px_rgba(0,0,0,0.05)] flex flex-col justify-center relative border-l-[5px] border-indigo-500">
          <h4 className="text-base font-semibold text-gray-700 mb-8 text-right flex items-center justify-end gap-2 [&_svg]:text-indigo-500 [&_svg]:w-6 [&_svg]:h-6">
            <BookIcon />
            Registered Units
          </h4>
          <div className="mb-6">
            <span className="text-7xl font-extrabold text-gray-800 leading-none">{registeredUnits}</span>
            <span className="text-gray-500 text-lg ml-2">out of {maxUnits} units</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded overflow-hidden mb-4">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          <div className={`flex items-center gap-2 text-sm font-medium ${registeredUnits >= maxUnits ? 'text-red-500' : 'text-emerald-500'} [&_svg]:w-4 [&_svg]:h-4`}>
            <CheckCircleIcon />
            {registeredUnits >= maxUnits
              ? "Maximum units reached"
              : `${maxUnits - registeredUnits} units remaining to maximum allowed`}
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          SEMESTER COURSE REGISTRATION
        </h2>
        <p className="text-gray-500">
          Select your courses for the semester. Please consult your department for guidance on required courses.
        </p>
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6">
        {data && Array.isArray(data) && data.map((course, i) => (
          <div
            key={course._id || i}
            className={`bg-white rounded-xl p-6 border flex flex-col relative overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_24px_rgba(0,0,0,0.05)] ${
              course.isRegistered
                ? 'border-l-4 border-emerald-500'
                : 'border-gray-200'
            }`}
          >
            <div className="text-sm text-gray-500 font-semibold mb-2 flex justify-between items-center">
              {course.code}
              {course.isRegistered && (
                <span className="flex items-center gap-1 text-emerald-500 text-xs font-semibold [&_svg]:w-3.5 [&_svg]:h-3.5">
                  <CheckCircleIcon />
                  Registered
                </span>
              )}
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-6 leading-relaxed flex-grow">
              {course.name}
            </h3>

            <div className="flex flex-col gap-3 mb-6">
              <div className="flex items-center gap-3 text-gray-600 text-sm [&_svg]:text-gray-400 [&_svg]:w-4 [&_svg]:h-4">
                <WeightIcon />
                <span className="bg-gray-100 px-3 py-1 rounded-full text-xs font-semibold text-gray-600">
                  {course.credit} Credit Units
                </span>
              </div>
              <div className="flex items-center gap-3 text-gray-600 text-sm [&_svg]:text-gray-400 [&_svg]:w-4 [&_svg]:h-4">
                <CalendarIcon />
                <span className="bg-gray-100 px-3 py-1 rounded-full text-xs font-semibold text-gray-600">
                  {`Level ${course.level || '100'} | ${course.semester || 'First'} Semester`}
                </span>
              </div>
            </div>

            <button
              className={`w-full py-3 rounded-lg font-semibold text-sm border-none cursor-pointer transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed ${
                course.isRegistered
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
              }`}
              onClick={() => course.isRegistered ? handleDrop(course._id) : handleRegister(course._id)}
              disabled={registerMutation.isPending || dropMutation.isPending}
            >
              {course.isRegistered ? 'Drop Course' : 'Register Course'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Registration;
